<template>
  <form @submit.prevent>
    <slot v-if="!!$slots.form" name="form" :validation-errors="state.validationErrors" />
    <div v-else>
      <template v-if="state.models">
        <YapLabeledInput
          v-for="field in state.formFields"
          :key="field.name"
          :label="field.title"
          :schema="field"
          :name="field.name"
          v-model="state.models[field.name]"
          :placeholder="field.description"
          :errors="state.validationErrors"
          :required="schema.required?.includes(field.name)"
        />
      </template>
      <slot />
    </div>
    <YapAlert
      v-if="state.errorMessage && !customErrorHandling"
      :message="t(state.errorMessage)"
      :type="AlertTypes.Error"
      class="mt-8"
    />
    <div class="mt-6">
      <slot
        name="actions"
        v-if="!!$slots.actions"
        :submit="submit"
        :submit-disabled="state.isSubmitDisabled"
        :submitting="state.submitting"
        :validation-errors="state.validationErrors"
      />

      <YapButton
        v-else
        class="uppercase"
        type="submit"
        :disabled="state.isSubmitDisabled"
        :loading="state.submitting"
        :flat="flat"
        :outlined="outlined"
        :button-style="submitButtonStyle"
        data-testid="submit-button"
        @click="submit"
      >
        {{ t(submitLabel ?? 'dict.label.send') }}
      </YapButton>
    </div>
  </form>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, useSlots, watch } from 'vue';

import apiGw from '@/services/apiGw';
import { useI18n } from '@/plugins/i18n';
import { Schema, Method } from '@/types/general';
import { SchemaItem } from '@/components/form/types/schemaItem';
import YapAlert, { AlertTypes } from '@/components/YapAlert.vue';
import YapButton, { ButtonStyle } from '@/components/YapButton.vue';
import YapLabeledInput from '@/components/form/YapLabeledInput.vue';

const props = withDefaults(
  defineProps<{
    schema: Schema;
    uri: string;
    method: Method;
    data?: any;
    submitLabel?: string;
    submitDisabled?: boolean;
    flat?: boolean;
    outlined?: boolean;
    customErrorHandling?: boolean;
    submitButtonStyle?: ButtonStyle;
    preSubmit?: () => void;
    dataFormatter?: (data: any) => any;
  }>(),
  {
    data: undefined,
    submitLabel: undefined,
    submitDisabled: undefined,
    submitButtonStyle: undefined,
    preSubmit: undefined,
    dataFormatter: undefined
  }
);

const emit = defineEmits(['submitted', 'validationErrors', 'errors']);

const { t } = useI18n();
const slots = useSlots();

interface State {
  submitting: boolean;
  validationErrors: Array<any>;
  formFields: Array<SchemaItem>;
  models?: { [key: string]: string | number | boolean };
  errorMessage?: string;
  isSubmitDisabled: boolean;
}

const state: State = reactive({
  submitting: false,
  validationErrors: [],
  formFields: computed(() => {
    return Object.keys(props.schema.properties).map((key) => {
      return {
        name: key,
        ...props.schema.properties[key]
      };
    });
  }),
  models: undefined,
  errorMessage: undefined,
  isSubmitDisabled: computed(() => {
    if (state.submitting) {
      return true;
    }
    if (!!slots.form && props.submitDisabled === undefined) {
      return false;
    } else if (props.submitDisabled !== undefined) {
      return props.submitDisabled;
    } else {
      if (!state.models) return true;
      return Object.entries(state.models)
        .filter(([key, value]) => {
          return props.schema.required?.includes(key);
        })
        .some(([key, value]) => {
          return !value || value === '';
        });
    }
  })
});

onMounted(() => {
  if (!!props.schema.properties) {
    setupModels();
  }
});

watch(
  () => props.schema.properties,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      setupModels();
    }
  }
);

function setupModels() {
  state.models = Object.assign(
    {},
    ...Object.keys(props.schema.properties).map((key) => {
      let defaultValue = undefined;
      if (props.schema.properties[key].default) {
        defaultValue = props.schema.properties[key].default;
      } else {
        switch (props.schema.properties[key].type) {
          case 'string':
            defaultValue = '';
            break;
          case 'boolean':
            defaultValue = false;
          default:
            defaultValue = '';
            break;
        }
      }
      return {
        [key]: defaultValue
      };
    })
  );
}

async function submit() {
  state.submitting = true;
  state.errorMessage = undefined;

  if (props.preSubmit) {
    try {
      await props.preSubmit();
    } catch (error: any) {
      if (error.status === 400 && error.response.data.error.name === 'ValidationError') {
        state.validationErrors = error.response.data.error.data;
        emit('validationErrors', state.validationErrors);
      }
      state.submitting = false;
      return;
    }
  }
  try {
    state.validationErrors = [];
    let inputData = props.data ?? state.models;
    if (!!props.dataFormatter) {
      inputData = props.dataFormatter(inputData);
    }
    const response = await apiGw[props.method](props.uri, inputData);
    emit('submitted', response, inputData);
  } catch (error: any) {
    if (error.status === 400 && error.response.data.error?.name === 'ValidationError') {
      state.validationErrors = error.response.data.error.data;
      emit('validationErrors', state.validationErrors);
    } else {
      state.errorMessage = 'app.error.general';
      emit('errors', error.response?.data);
    }
  }
  state.submitting = false;
}
</script>
